import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type {
  AnyFieldSpecBase,
  ScopeSection as SchemaSection
} from 'v5/core/form-schema/formSchema.types';

type ScopeSection = SchemaSection<AnyFieldSpecBase>;
import { whenAnimationsSettle } from 'v5/platform/dom/whenAnimationsSettle';

/** Attribute the section views carry so navigation can find them. */
export const SECTION_ID_ATTRIBUTE = 'data-section-id';

/** Space kept above a section when scrolled into view (the sticky tab bar with its top bleed). */
export const SECTION_SCROLL_MARGIN_CLASS = 'scroll-mt-17';
const SECTION_TOP_OFFSET_PX = 68;

function findScrollParent(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
}

/** The view of `sectionId` under `root` (see `SECTION_ID_ATTRIBUTE`). */
export function sectionElement(
  root: HTMLElement,
  sectionId: string
): HTMLElement | null {
  // Section ids use `[a-z0-9:/_-]`; only quotes and backslashes would need
  // escaping inside a quoted attribute selector (no `CSS.escape` in jsdom).
  const escaped = sectionId.replace(/["\\]/g, '\\$&');
  return root.querySelector<HTMLElement>(
    `[${SECTION_ID_ATTRIBUTE}="${escaped}"]`
  );
}

export type SectionNavigation = {
  /** Put on the element that wraps every section view. */
  rootRef: RefObject<HTMLDivElement>;
  /** Section currently at the top of the scroll container (`null` = none). */
  activeId: string | null;
  /** Expands the section and, once it is open, scrolls it under the tab bar. */
  goTo(sectionId: string): void;
  /** Open state per section, falling back to `defaultExpanded`. */
  isOpen(section: ScopeSection): boolean;
  setOpen(sectionId: string, open: boolean): void;
};

/**
 * Tab-style navigation between scope sections: the section the user is at —
 * under the mouse pointer or holding the keyboard focus, whichever moved last
 * — else scroll-spy over the nearest scroll container, plus "go to section"
 * that expands and scrolls. With several sections in view, the one the user
 * is at is the one they mean. Sections that appear later (membership picker)
 * fall back to their default state.
 */
/**
 * "Go to" navigates for at least this long, and while its smooth scroll keeps
 * scrolling (up to `NAVIGATION_MAX_MS`): the chosen section keeps the
 * highlight meanwhile, whatever passes under the pointer.
 */
const PIN_GRACE_MS = 800;
const NAVIGATION_MAX_MS = 2000;
/** The navigation ends this long after its scroll's last frame. */
const SCROLL_SETTLE_MS = 150;

type Pinned = {
  id: string;
  /** Navigating until then; `0` once the user took over (wheel, focus). */
  until: number;
  latest: number;
};

/** Where the user is at among the sections (see `useSectionNavigation`). */
type UserAt = {
  /** The mouse, while over the sections. */
  pointer: { x: number; y: number } | null;
  /** The section the mouse was last over (kept across the gap between two). */
  hoveredId: string | null;
  focusedId: string | null;
  /** Which of the two the user moved last; `null` right after "go to". */
  lastInput: 'pointer' | 'focus' | null;
};

function isNavigating(pinned: Pinned | null): pinned is Pinned {
  return pinned !== null && Date.now() < pinned.until;
}

export function useSectionNavigation(
  sections: readonly ScopeSection[],
  /**
   * How far below the scroll container's top a section's top may be and still
   * count as the one in view: under a sticky tab bar by default.
   */
  topOffsetPx: number = SECTION_TOP_OFFSET_PX
): SectionNavigation {
  const rootRef = useRef<HTMLDivElement>(null);
  const [openById, setOpenById] = useState<Record<string, boolean>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  // A section chosen from the tabs stays highlighted (short sections never
  // reach the top) until the user scrolls it out of view or points elsewhere.
  const pinnedRef = useRef<Pinned | null>(null);
  const userAtRef = useRef<UserAt>({
    pointer: null,
    hoveredId: null,
    focusedId: null,
    lastInput: null
  });
  const sectionIds = useMemo(
    () => sections.map(section => section.id),
    [sections]
  );
  const idsKey = sectionIds.join('\0');

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const scroller = findScrollParent(root);
    let frame = 0;
    // The wheel hands the highlight to what is under the pointer, or else in
    // view; it and the focus also end a "go to" still under way.
    const at = userAtRef.current;
    const sectionIdOf = (target: EventTarget | Element | null) =>
      target instanceof Element
        ? (target
            .closest(`[${SECTION_ID_ATTRIBUTE}]`)
            ?.getAttribute(SECTION_ID_ATTRIBUTE) ?? null)
        : null;
    // A select's list or a menu opened from a section is portalled out of
    // `root`: going there is still being at that section.
    const isInOwnPopup = (target: EventTarget | null) =>
      target instanceof Node &&
      Array.from(
        root.querySelectorAll('[aria-expanded="true"][aria-controls]')
      ).some(trigger =>
        document
          .getElementById(trigger.getAttribute('aria-controls') ?? '')
          ?.contains(target)
      );

    const compute = () => {
      frame = 0;
      const ids = idsKey === '' ? [] : idsKey.split('\0');
      if (ids.length === 0) {
        setActiveId(null);
        return;
      }
      const pinned = pinnedRef.current;
      if (isNavigating(pinned) && ids.includes(pinned.id)) {
        setActiveId(pinned.id);
        return;
      }
      if (at.pointer) {
        // Content scrolled under a pointer that stayed put.
        const under =
          document.elementFromPoint?.(at.pointer.x, at.pointer.y) ?? null;
        const id = under && root.contains(under) ? sectionIdOf(under) : null;
        if (id !== null) at.hoveredId = id;
      }
      const atId =
        at.lastInput === 'pointer'
          ? at.hoveredId
          : at.lastInput === 'focus'
            ? at.focusedId
            : null;
      if (atId !== null && ids.includes(atId)) {
        setActiveId(atId);
        return;
      }
      // Hidden (e.g. the wizard is on another step): no geometry to read;
      // the resize observer recomputes once the form is shown.
      if (root.offsetHeight === 0) return;
      if (!scroller) {
        setActiveId(ids[0]);
        return;
      }
      const scrollerRect = scroller.getBoundingClientRect();
      const scrollerTop = scrollerRect.top;
      if (pinned) {
        const element = sectionElement(root, pinned.id);
        const rect = element?.getBoundingClientRect();
        const inView =
          rect !== undefined &&
          rect.bottom > scrollerTop + topOffsetPx &&
          rect.top < scrollerRect.bottom;
        if (inView) {
          setActiveId(pinned.id);
          return;
        }
        pinnedRef.current = null;
      }
      const atBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      let current = ids[0];
      if (atBottom) {
        current = ids[ids.length - 1];
      } else {
        for (const id of ids) {
          const element = sectionElement(root, id);
          if (!element) continue;
          const top = element.getBoundingClientRect().top - scrollerTop;
          if (top <= topOffsetPx + 1) current = id;
          else break;
        }
      }
      setActiveId(current);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(compute);
    };
    const onScroll = () => {
      const pinned = pinnedRef.current;
      if (isNavigating(pinned)) {
        // Its smooth scroll is still going: keep navigating till it settles.
        pinned.until = Math.min(
          pinned.latest,
          Math.max(pinned.until, Date.now() + SCROLL_SETTLE_MS)
        );
      }
      schedule();
    };
    /** The user took over from a "go to" still under way. */
    const endNavigation = () => {
      const pinned = pinnedRef.current;
      if (pinned) pinned.until = 0;
    };
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      at.pointer = { x: event.clientX, y: event.clientY };
      // Passing over sections while "go to" scrolls is no choice of theirs.
      if (isNavigating(pinnedRef.current)) return;
      at.hoveredId = sectionIdOf(event.target) ?? at.hoveredId;
      at.lastInput = 'pointer';
      schedule();
    };
    const onPointerLeave = (event: PointerEvent) => {
      if (isInOwnPopup(event.relatedTarget)) return;
      at.pointer = null;
      at.hoveredId = null;
      at.lastInput = at.focusedId !== null ? 'focus' : null;
      schedule();
    };
    const onFocusIn = (event: FocusEvent) => {
      endNavigation();
      at.focusedId = sectionIdOf(event.target);
      at.lastInput = 'focus';
      schedule();
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && root.contains(next)) return;
      if (isInOwnPopup(next)) return;
      at.focusedId = null;
      if (at.lastInput === 'focus') at.lastInput = null;
      schedule();
    };
    const onWheel = () => {
      endNavigation();
      at.lastInput = 'pointer';
    };

    compute();
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    scroller?.addEventListener('wheel', onWheel, { passive: true });
    root.addEventListener('pointermove', onPointerMove);
    root.addEventListener('pointerleave', onPointerLeave);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(schedule);
    resizeObserver?.observe(root);
    return () => {
      scroller?.removeEventListener('scroll', onScroll);
      scroller?.removeEventListener('wheel', onWheel);
      root.removeEventListener('pointermove', onPointerMove);
      root.removeEventListener('pointerleave', onPointerLeave);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      resizeObserver?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [idsKey, topOffsetPx]);

  const goTo = useCallback((sectionId: string) => {
    setOpenById(prev => ({ ...prev, [sectionId]: true }));
    const now = Date.now();
    const navigation: Pinned = {
      id: sectionId,
      until: now + PIN_GRACE_MS,
      latest: now + NAVIGATION_MAX_MS
    };
    pinnedRef.current = navigation;
    // A section pointed at or focused before is not the one chosen now; the
    // pointer takes over again once it moves after the navigation.
    userAtRef.current.hoveredId = null;
    userAtRef.current.lastInput = null;
    setActiveId(sectionId);
    // Measure where to scroll once the section is laid out in full: a
    // collapsed one grows open for a while, and until then a section near the
    // end has too little below it to reach the tab bar — the scroll would stop
    // short and leave the rest of the section out of view.
    requestAnimationFrame(() => {
      const root = rootRef.current;
      const element = root && sectionElement(root, sectionId);
      if (!element) return;
      void whenAnimationsSettle(element).then(() => {
        // Not if another "go to" came meanwhile or the user took over (wheel,
        // focus).
        if (pinnedRef.current !== navigation || navigation.until === 0) return;
        element.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      });
    });
  }, []);

  const isOpen = useCallback(
    (section: ScopeSection) => openById[section.id] ?? section.defaultExpanded,
    [openById]
  );
  const setOpen = useCallback((sectionId: string, open: boolean) => {
    setOpenById(prev => ({ ...prev, [sectionId]: open }));
  }, []);

  return useMemo(
    () => ({ rootRef, activeId, goTo, isOpen, setOpen }),
    [activeId, goTo, isOpen, setOpen]
  );
}
